// frontend/super-admin/src/pages/Dashboard.jsx
import React, { useEffect, useState } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Statistic, 
  Table, 
  Tag, 
  Spin, 
  Badge, 
  Button,
  Space,
  Alert
} from 'antd';
import { 
  UserOutlined, 
  TeamOutlined, 
  ClockCircleOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

export default function Dashboard() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingApprovals, setPendingApprovals] = useState(0);
  const [pendingPayments, setPendingPayments] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch tenants
      const tenantsRes = await api.get('/api/admin/tenants');
      setTenants(tenantsRes.data.data);

      // ✅ NEW: Fetch pending approvals count
      try {
        const approvalsRes = await api.get('/api/admin/pending-approvals/count');
        setPendingApprovals(approvalsRes.data.count || 0);
      } catch (err) {
        console.warn('Pending approvals endpoint not available:', err.message);
        // Fallback: count from tenants array
        setPendingApprovals(
          tenantsRes.data.data.filter(t => t.approvalStatus === 'pending').length
        );
      }

      // ✅ NEW: Fetch pending payments count
      try {
        const paymentsRes = await api.get('/api/payment-requests/pending/count');
        setPendingPayments(paymentsRes.data.count || 0);
      } catch (err) {
        console.warn('Payment requests endpoint not available:', err.message);
        setPendingPayments(0);
      }

    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Business Name',
      dataIndex: 'businessName',
      key: 'businessName',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Plan',
      dataIndex: 'plan',
      key: 'plan',
      render: (plan) => (
        <Tag color="blue">{plan?.toUpperCase() || 'FREE'}</Tag>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {status?.toUpperCase()}
        </Tag>
      )
    },
    // ✅ NEW: Approval Status Column
    {
      title: 'Approval',
      dataIndex: 'approvalStatus',
      key: 'approvalStatus',
      render: (approvalStatus) => {
        const config = {
          pending: { color: 'orange', text: 'PENDING' },
          approved: { color: 'green', text: 'APPROVED' },
          rejected: { color: 'red', text: 'REJECTED' }
        };
        const c = config[approvalStatus] || config.pending;
        
        return (
          <Tag color={c.color}>{c.text}</Tag>
        );
      }
    }
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <h1 style={{ marginBottom: 24 }}>📊 Dashboard</h1>

      {/* ✅ NEW: Alert for Pending Items */}
      {(pendingApprovals > 0 || pendingPayments > 0) && (
        <Alert
          message="⚠️ Action Required"
          description={
            <Space direction="vertical" size={4}>
              {pendingApprovals > 0 && (
                <div>
                  <strong>{pendingApprovals}</strong> tenant{pendingApprovals > 1 ? 's' : ''} waiting for approval
                  <Button 
                    type="link" 
                    size="small"
                    onClick={() => navigate('/tenants')}
                  >
                    View →
                  </Button>
                </div>
              )}
              {pendingPayments > 0 && (
                <div>
                  <strong>{pendingPayments}</strong> payment{pendingPayments > 1 ? 's' : ''} pending verification
                  <Button 
                    type="link" 
                    size="small"
                    onClick={() => navigate('/payment-requests')}
                  >
                    View →
                  </Button>
                </div>
              )}
            </Space>
          }
          type="warning"
          showIcon
          closable
          style={{ marginBottom: 24 }}
        />
      )}

      {/* Statistics */}
      <Row gutter={16}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Tenants"
              value={tenants.length}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Active Tenants"
              value={tenants.filter(t => t.status === 'active').length}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>

        {/* ✅ NEW: Pending Approvals Card */}
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable onClick={() => navigate('/tenants')} style={{ cursor: 'pointer' }}>
            <Badge count={pendingApprovals} offset={[-10, 10]}>
              <Statistic
                title="Pending Approvals"
                value={pendingApprovals}
                prefix={<ClockCircleOutlined />}
                valueStyle={{ color: '#faad14' }}
              />
            </Badge>
          </Card>
        </Col>

        {/* ✅ NEW: Pending Payments Card */}
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable onClick={() => navigate('/payment-requests')} style={{ cursor: 'pointer' }}>
            <Badge count={pendingPayments} offset={[-10, 10]}>
              <Statistic
                title="Pending Payments"
                value={pendingPayments}
                prefix={<DollarOutlined />}
                valueStyle={{ color: '#ff4d4f' }}
              />
            </Badge>
          </Card>
        </Col>
      </Row>

      {/* Recent Tenants */}
      <Card title="📋 All Tenants" style={{ marginTop: 24 }}>
        <Table 
          dataSource={tenants} 
          columns={columns} 
          rowKey="id"
          pagination={{ pageSize: 10 }}
        />
      </Card>
    </div>
  );
}